import type React from "react";
import { useState } from "react";

import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import { usePutCareEventBilling } from "api/hooks/usePutCareEventBilling";
import type { Status } from "api/schemas/CareEventBilling";
import { StatusUpdateDialog } from "routes/admin-billing/components";
import { BaseButton } from "shared/atoms/BaseButton";
import { reportError } from "utils/errorReporting";

interface Props {
  cid: number;
  clearSelectedCareEvent?: () => void;
}

export const MedicalNoteFormButtonsAdminBilling: React.FC<Props> = ({ cid, clearSelectedCareEvent }) => {
  const { t } = useTranslation();
  const [showMarkAsCompleted, setShowMarkAsCompleted] = useState(false);
  const [showMarkAsDiscarded, setShowMarkAsDiscarded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync, isLoading } = usePutCareEventBilling();

  const updateCareEventBillingStatus = async (careEventID: number, status: Status) => {
    mutateAsync(
      {
        data: { care_event_ids: [careEventID], status },
      },
      {
        onSuccess: () => {
          if (clearSelectedCareEvent) {
            clearSelectedCareEvent();
          }
          setShowMarkAsCompleted(false);
          setShowMarkAsDiscarded(false);
        },
        onError: e => {
          reportError("AppointmentDetails.tsx", e);
          setError(t("errors.generic"));
        },
      }
    );
  };

  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent={{ md: "flex-end" }}
        gap={2}
        m={4}
        sx={{ "& button": { maxWidth: { md: "400px" }, flex: { md: 0 } } }}
      >
        <BaseButton
          text={t("admin_billing.buttons.discard")}
          onClick={() => setShowMarkAsDiscarded(true)}
          variant="tertiary"
          uppercase
        />
        <BaseButton text={t("admin_billing.buttons.complete")} onClick={() => setShowMarkAsCompleted(true)} />
      </Stack>

      <StatusUpdateDialog
        open={showMarkAsCompleted}
        status="completed"
        count={1}
        isLoading={isLoading}
        error={error}
        onConfirm={() => updateCareEventBillingStatus(cid, "completed")}
        onCancel={() => setShowMarkAsCompleted(false)}
      />

      <StatusUpdateDialog
        open={showMarkAsDiscarded}
        status="discarded"
        count={1}
        isLoading={isLoading}
        error={error}
        onConfirm={() => updateCareEventBillingStatus(cid, "discarded")}
        onCancel={() => setShowMarkAsDiscarded(false)}
      />
    </>
  );
};
